import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();
const STORAGE_KEY = 'musb_cart';

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [isCartBouncing, setIsCartBouncing] = useState(false);
  const [isBadgePopping, setIsBadgePopping] = useState(false);
  const [flyingParticles, setFlyingParticles] = useState([]);

  // Persist to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cartItems));
    } catch {}
  }, [cartItems]);

  const isInCart = (testId) => {
    if (!testId) return false;
    return cartItems.some((item) => String(item.id || item._id) === String(testId));
  };

  const addToCart = (test) => {
    if (!test) return;
    setCartItems((prev) => {
      const testId = test.id || test._id;
      const existing = prev.find((item) => String(item.id || item._id) === String(testId));
      if (existing) {
        return prev;
      }
      return [...prev, { ...test, id: testId, quantity: 1 }];
    });
  };

  const removeFromCart = (testId) => {
    setCartItems((prev) => prev.filter((item) => String(item.id || item._id) !== String(testId)));
  };

  const toggleCart = (test) => {
    const testId = test.id || test._id;
    if (isInCart(testId)) {
      removeFromCart(testId);
      return false;
    } else {
      addToCart(test);
      return true;
    }
  };

  const clearCart = () => {
    setCartItems([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  const triggerAddToCartAnimation = (eventOrRect, test, options = {}) => {
    if (!test) return;

    // Check prefers-reduced-motion
    const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Determine source element bounding rect
    let sourceRect = null;
    if (eventOrRect) {
      if (eventOrRect.getBoundingClientRect) {
        sourceRect = eventOrRect.getBoundingClientRect();
      } else if (eventOrRect.currentTarget && eventOrRect.currentTarget.getBoundingClientRect) {
        sourceRect = eventOrRect.currentTarget.getBoundingClientRect();
      } else if (eventOrRect.target && eventOrRect.target.getBoundingClientRect) {
        sourceRect = eventOrRect.target.getBoundingClientRect();
      } else if (typeof eventOrRect.left === 'number') {
        sourceRect = eventOrRect;
      }
    }

    // Target cart element (Floating Cart button preferred if visible, otherwise Navbar Cart button)
    const floatCartEl = typeof document !== 'undefined' ? (document.getElementById('floating-cart-btn') || document.querySelector('.floating-cart-btn')) : null;
    const navCartEl = typeof document !== 'undefined' ? (document.getElementById('navbar-cart-btn') || document.querySelector('.nav-cart-btn')) : null;
    const targetEl = floatCartEl || navCartEl;

    let targetRect = null;
    if (targetEl && targetEl.getBoundingClientRect) {
      targetRect = targetEl.getBoundingClientRect();
    } else {
      targetRect = {
        left: typeof window !== 'undefined' ? window.innerWidth - 60 : 300,
        top: 20,
        width: 40,
        height: 40,
      };
    }

    const winW = typeof window !== 'undefined' ? window.innerWidth : 800;
    const winH = typeof window !== 'undefined' ? window.innerHeight : 600;

    const startX = sourceRect ? sourceRect.left + sourceRect.width / 2 - 20 : winW / 2 - 20;
    const startY = sourceRect ? sourceRect.top + sourceRect.height / 2 - 20 : winH / 2 - 20;
    const endX = targetRect.left + targetRect.width / 2 - 20;
    const endY = targetRect.top + targetRect.height / 2 - 20;

    // Add item to cart state
    addToCart(test);

    // Reduced motion handling
    if (prefersReducedMotion) {
      setIsCartBouncing(true);
      setIsBadgePopping(true);
      setTimeout(() => {
        setIsCartBouncing(false);
        setIsBadgePopping(false);
      }, 400);
      return;
    }

    // Spawn flying particle
    const particleId = Date.now() + Math.random();
    const particleImage = options.image || test.image || '/assets/lavender_tube.png';

    const newParticle = {
      id: particleId,
      startX,
      startY,
      endX,
      endY,
      midX: (startX + endX) / 2 + (startX < endX ? -30 : 30),
      midY: Math.min(startY, endY) - 100, // Arc upwards
      image: particleImage,
      name: test.title || test.name || 'Lab Test',
    };

    setFlyingParticles((prev) => [...prev, newParticle]);

    // Duration of parabolic flight is ~480ms
    setTimeout(() => {
      setIsCartBouncing(true);
      setIsBadgePopping(true);

      setFlyingParticles((prev) => prev.filter((p) => p.id !== particleId));

      setTimeout(() => {
        setIsCartBouncing(false);
        setIsBadgePopping(false);
      }, 400);
    }, 480);
  };

  const cartCount = cartItems.length;
  const cartTotal = cartItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        toggleCart,
        isInCart,
        clearCart,
        cartCount,
        cartTotal,
        isCartBouncing,
        isBadgePopping,
        flyingParticles,
        triggerAddToCartAnimation,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
